import { bolt, figure, groundRing, sabre, shield, slash, sprite, star } from '../kit';
import { type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ดาบชักเร็วโคมเหล็ก (ทางโคมเหล็ก, yang): the senior wardens' quick-draw sabre. A slim blade leaving a
 * black-lacquer scabbard, lamp-gold cuts and the bright static crackle (อัสนีชา) every cut leaves behind.
 */

const STEEL = '#e8eef6';
const HILT = '#ffcf5a';
const SHEATH = '#2e2a34';
const SHEATH_TRIM = '#c8862a';
const SPARK = '#fff0a8';
const LAMP = '#ffb84a';
const IRON = '#8a94a6';
const CORD = '#d8452a';

const quick = (p: Pix, x0: number, y0: number, x1: number, y1: number, curve = -1.6) =>
  sabre(p, x0, y0, x1, y1, { hilt: HILT, blade: STEEL, w: 3, curve });

/** The scabbard along (x0,y0)-(x1,y1), mouth at the far end. */
function scabbard(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  p.thick(x0, y0, x1, y1, SHEATH, 3);
  p.line(x0, y0, x0 + (x1 - x0) * 0.15, y0 + (y1 - y0) * 0.15, SHEATH_TRIM);
  p.rect(Math.round(x1) - 1, Math.round(y1) - 1, 3, 3, SHEATH_TRIM);
}

/** A small crackle of static: a 3-bend spark. */
function crackle(p: Pix, x: number, y: number): void {
  p.path([
    [x, y],
    [x + 2, y + 1],
    [x + 1, y + 3],
    [x + 3, y + 4],
  ], SPARK);
}

/** A little hanging lamp-ember (falls like a burnt wick). */
function ember(p: Pix, x: number, y: number): void {
  p.rect(x, y, 2, 2, LAMP).px(x, y, SPARK).px(x + 1, y + 3, LAMP);
}

registerIcons('skill', {
  // Draw, cut, sheathe: the blade half out, a spark running down its edge.
  'sp-flash': (p) => {
    scabbard(p, 3, 28, 13, 18);
    quick(p, 13, 18, 28, 3);
    crackle(p, 20, 6);
    crackle(p, 24, 13);
    slash(p, 13, 18, 13, -1.9, -0.2, LAMP, 2);
  },
  // The point thrust through an iron plate; sparks spray from the hole.
  'sp-pierce': (p) => {
    shield(p, 17, 3, 12, 15, IRON, '#d0d8e4');
    quick(p, 2, 10, 29, 10, 0);
    for (const [x, y] of [
      [24, 4],
      [26, 14],
      [12, 18],
    ] as const)
      crackle(p, x - 1, y);
  },
  // Three draws in a row, cuts drifting down like wick-sparks.
  'sp-petals': (p) => {
    for (let k = 0; k < 3; k++) slash(p, 10 + k * 7, 8 + k * 3, 7, -2.4, -0.6, k === 2 ? SPARK : LAMP, 2);
    quick(p, 3, 29, 15, 17, -1);
    ember(p, 22, 20);
    ember(p, 27, 24);
    ember(p, 18, 26);
  },
  // A storm of static on a chosen circle.
  'sp-bloom': (p) => {
    groundRing(p, 16, 22, 13, 6, LAMP);
    for (let k = 0; k < 6; k++) {
      const a = (k * Math.PI) / 3 + 0.3;
      crackle(p, 15 + Math.cos(a) * 9, 18 + Math.sin(a) * 4 - 2);
    }
    quick(p, 9, 18, 24, 3, -1.4);
    star(p, 16, 21, 2, SPARK);
  },
  // Gone and back at another's side: fading figures along a dotted path, the last one striking.
  'sp-hunt': (p) => {
    figure(p, 2, 18, '#5a5a6a', 'run');
    figure(p, 10, 14, '#8a8a9a', 'run');
    figure(p, 18, 10, '#d0d0dc', 'run');
    quick(p, 23, 14, 30, 2, -0.8);
    p.rect(3, 29, 2, 1, LAMP).rect(9, 27, 2, 1, LAMP).rect(15, 25, 2, 1, LAMP);
  },
  // The verdict: three blades close from three sides, a stroke of static falls on the middle.
  'sp-verdict': (p) => {
    quick(p, 2, 28, 13, 18, -0.8);
    quick(p, 30, 28, 19, 18, 0.8);
    quick(p, 16, 30, 16, 20, 0);
    bolt(p, [
      [18, 1],
      [14, 7],
      [18, 9],
      [15, 15],
    ], LAMP, 2);
  },
  // Loyal heart: the cuffs fall away, the lamp burns steady.
  'sp-loyal': (p) => {
    sprite(
      p,
      13,
      5,
      ['....i....', '...i.i...', '..iiiii..', '.iiiiiii.', '.issssi.', '.illlli.', '.issssi.', '.illlli.', '.iiiiiii.', '..iiiii..', '...ccc...', '...c.c...'],
      { i: IRON, l: LAMP, s: SPARK, c: CORD },
      { k: 1 },
    );
    p.ring(6, 25, 3, 2, IRON);
    p.ring(26, 25, 3, 2, IRON);
    p.erase(9, 25).erase(23, 25);
    for (const x of [4, 28]) p.rect(x, 20, 1, 2, SPARK);
    p.ring(17, 11, 9, 9, LAMP, Math.PI * 1.1, Math.PI * 1.9);
  },
  // The sabre raised, three strokes of static fall on a chosen ring.
  'sp-decree': (p) => {
    groundRing(p, 17, 25, 11, 4, LAMP);
    for (const [x, d] of [
      [9, 1],
      [17, -1],
      [25, 1],
    ] as const)
      bolt(p, [
        [x + d * 2, 4],
        [x - d, 11],
        [x + d * 2, 13],
        [x, 22],
      ], LAMP, 2);
    quick(p, 4, 29, 5, 9, 0.6);
  },
});
